/**
 * Phase 14.5 Step 2 — `useCampaignRegistries` parallel-fetch hook.
 *
 * Fetches all three live registry endpoints used by the unified
 * wizard:
 *
 *   • pk          — GET /api/pk-campaigns/registry?scenario_id=<id?>  (Phase 10.1, 33 vars)
 *   • geometry    — GET /api/geometry-campaigns/registry              (Phase 13, registry_version="13.0")
 *   • degradation — GET /api/degradation-profiles/registry            (Phase 14, profile_version="14.0")
 *
 * Module-level cache keyed by `scenarioId || "__default__"` so
 * multiple consumers (the wizard host + each section) share results
 * within one render. Caches per-key forever inside the SPA session.
 *
 * Returns: `{ loading, error, registries: { pk, geometry, degradation } }`.
 *   • `loading` is true until all three settle.
 *   • `error` is the first rejection encountered (or null).
 *   • Each entry in `registries` is the registry payload OR null if
 *     that fetch rejected.
 */
import { useEffect, useRef, useState } from "react";
import pkCampaigns from "@/api/pkCampaigns";
import geometryCampaigns from "@/api/geometryCampaigns";
import degradationProfiles from "@/api/degradationProfiles";

const _cache = new Map();

function _cacheKey(scenarioId) {
    return scenarioId || "__default__";
}

export function _resetRegistryCache() {
    // Test helper — safe to call without consequences in production.
    _cache.clear();
}

export default function useCampaignRegistries({ scenarioId } = {}) {
    const [state, setState] = useState({
        loading: true,
        error: null,
        registries: { pk: null, geometry: null, degradation: null },
    });
    const cancelledRef = useRef(false);

    useEffect(() => {
        cancelledRef.current = false;

        const cacheKey = _cacheKey(scenarioId);
        if (_cache.has(cacheKey)) {
            setState(_cache.get(cacheKey));
            return undefined;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        const tasks = [
            pkCampaigns.registry(scenarioId ? { scenario_id: scenarioId } : {}),
            geometryCampaigns.registry(),
            degradationProfiles.registry(),
        ];

        Promise.allSettled(tasks).then((results) => {
            if (cancelledRef.current) return;
            const [pkRes, geomRes, degRes] = results;
            const rejected = results.find((r) => r.status === "rejected");
            const next = {
                loading: false,
                error: rejected ? (rejected.reason ?? new Error("registry fetch failed")) : null,
                registries: {
                    pk: pkRes.status === "fulfilled" ? pkRes.value : null,
                    geometry: geomRes.status === "fulfilled" ? geomRes.value : null,
                    degradation: degRes.status === "fulfilled" ? degRes.value : null,
                },
            };
            // Only cache if all three resolved cleanly. Partial failures
            // re-fetch on the next consumer mount so a transient network
            // glitch doesn't pin the wizard in a half-loaded state.
            if (!rejected) _cache.set(cacheKey, next);
            setState(next);
        });

        return () => {
            cancelledRef.current = true;
        };
    }, [scenarioId]);

    return state;
}
